import React, { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./App.css";
import { PrivacyPolicyPage, SmsTermsPage } from "./LegalPages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEPOSIT_LINK = "https://square.link/u/xVaxRD0b";
const RENTAL_PAYMENT_LINK = "https://checkout.square.site/merchant/MLW0Q0QH5KHTN/checkout/CC4HW34JB5SCTCQWAQNCHZWR";

// Customer auth helpers (JWT stored in localStorage)
const getAuth = () => {
  try {
    const token = localStorage.getItem('srv_token');
    const user = JSON.parse(localStorage.getItem('srv_user') || 'null');
    return token && user ? { token, user } : null;
  } catch (e) {
    return null;
  }
};
const setAuthSession = (token, user) => {
  localStorage.setItem('srv_token', token);
  localStorage.setItem('srv_user', JSON.stringify(user));
};
const clearAuthSession = () => {
  localStorage.removeItem('srv_token');
  localStorage.removeItem('srv_user');
};

// Log backend URL on startup for debugging
console.log('🔧 Shoreline RV Rental - Backend Configuration:');
console.log('   BACKEND_URL:', BACKEND_URL);
console.log('   API Base:', API);

// Dynamic Photo Gallery Component - loads images from server
const PhotoGallery = ({ trailer, title }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Fetch images from PHP API on Bluehost
  useEffect(() => {
    const fetchImages = async () => {
      try {
        // Try to fetch from PHP API (production on Bluehost)
        const response = await fetch(`/api/images.php?trailer=${trailer}`);
        if (response.ok) {
          const data = await response.json();
          if (data.images && data.images.length > 0) {
            setImages(data.images);
            console.log(`📷 Loaded ${data.count} images for ${trailer} from API`);
          } else {
            // Fallback to default images
            setImages(getDefaultImages(trailer));
          }
        } else {
          // Fallback to default images
          setImages(getDefaultImages(trailer));
        }
      } catch (error) {
        console.log(`📷 Using default images for ${trailer} (API not available)`);
        setImages(getDefaultImages(trailer));
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, [trailer]);

  // Default images fallback
  const getDefaultImages = (trailerType) => {
    if (trailerType === 'starcraft') {
      return [
        { id: 1, src: "/images/starcraft/StarPic1.png", alt: "Starcraft Exterior" },
        { id: 2, src: "/images/starcraft/StarPic2.png", alt: "Starcraft Interior Living" },
        { id: 3, src: "/images/starcraft/StarPic3.png", alt: "Starcraft Bedroom" },
        { id: 4, src: "/images/starcraft/StarPic4.png", alt: "Starcraft Kitchen" },
        { id: 5, src: "/images/starcraft/StarPic5.png", alt: "Starcraft Bunk Area" },
      ];
    } else {
      return [
        { id: 1, src: "/images/amerilite/AmerPic1.png", alt: "Ameri-lite Exterior" },
        { id: 2, src: "/images/amerilite/AmerPic2.png", alt: "Ameri-lite Interior" },
        { id: 3, src: "/images/amerilite/AmerPic3.png", alt: "Ameri-lite Living Space" },
        { id: 4, src: "/images/amerilite/AmerPic4.png", alt: "Ameri-lite Setup" },
        { id: 5, src: "/images/amerilite/AmerPic5.png", alt: "Ameri-lite View" },
      ];
    }
  };

  const nextImage = useCallback(() => {
    if (images.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }
  }, [images.length]);

  const prevImage = useCallback(() => {
    if (images.length > 0) {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  }, [images.length]);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="photo-gallery" data-testid={`gallery-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="gallery-loading">
          <i className="fas fa-spinner fa-spin"></i> Loading photos...
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="photo-gallery" data-testid={`gallery-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="gallery-empty">No photos available</div>
      </div>
    );
  }

  return (
    <div className="photo-gallery" data-testid={`gallery-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="gallery-main">
        <button className="gallery-nav prev" onClick={prevImage} data-testid="gallery-prev">
          <i className="fas fa-chevron-left"></i>
        </button>
        <img
          src={images[currentIndex]?.src}
          alt={images[currentIndex]?.alt}
          onClick={() => openLightbox(currentIndex)}
          className="gallery-main-image"
          data-testid="gallery-main-image"
        />
        <button className="gallery-nav next" onClick={nextImage} data-testid="gallery-next">
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
      
      <div className="gallery-thumbnails">
        {images.map((image, index) => (
          <img
            key={image.id}
            src={image.src}
            alt={image.alt}
            className={`gallery-thumbnail ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
            data-testid={`thumbnail-${index}`}
          />
        ))}
      </div>
      
      <div className="gallery-counter">
        {currentIndex + 1} / {images.length}
      </div>

      {lightboxOpen && (
        <div className="lightbox" onClick={() => setLightboxOpen(false)} data-testid="lightbox">
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxOpen(false)}>
              <i className="fas fa-times"></i>
            </button>
            <button className="lightbox-nav prev" onClick={prevImage}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <img src={images[currentIndex]?.src} alt={images[currentIndex]?.alt} />
            <button className="lightbox-nav next" onClick={nextImage}>
              <i className="fas fa-chevron-right"></i>
            </button>
            <div className="lightbox-counter">{currentIndex + 1} / {images.length}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Backend wake-up utility for cold start handling
const backendStatus = {
  isAwake: false,
  lastCheck: null,
  wakeupInProgress: false
};

// Wake up the backend with retries (handles cold starts)
const wakeUpBackend = async (maxRetries = 3) => {
  if (backendStatus.isAwake && backendStatus.lastCheck && (Date.now() - backendStatus.lastCheck < 60000)) {
    console.log('✅ Backend already awake (cached)');
    return true; // Backend was recently confirmed awake
  }
  
  if (backendStatus.wakeupInProgress) {
    // Wait for existing wakeup to complete
    console.log('⏳ Wake-up already in progress, waiting...');
    await new Promise(resolve => setTimeout(resolve, 2000));
    return backendStatus.isAwake;
  }
  
  backendStatus.wakeupInProgress = true;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const url = `${API}/health`;
      console.log(`🔄 Backend wake-up attempt ${attempt}/${maxRetries}... URL: ${url}`);
      const response = await axios.get(url, { 
        timeout: attempt === 1 ? 10000 : 20000 // Longer timeout on retries
      });
      console.log('📡 Response:', response.data);
      if (response.data.status === 'healthy') {
        backendStatus.isAwake = true;
        backendStatus.lastCheck = Date.now();
        backendStatus.wakeupInProgress = false;
        console.log('✅ Backend is awake and healthy');
        return true;
      }
    } catch (error) {
      console.log(`❌ Wake-up attempt ${attempt} failed:`, error.message);
      console.log('   Error details:', error.code, error.response?.status);
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2s between retries
      }
    }
  }
  
  backendStatus.wakeupInProgress = false;
  console.log('❌ All wake-up attempts failed');
  return false;
};

// API call wrapper with automatic retry for cold starts
const apiCall = async (method, url, data = null, options = {}) => {
  const { timeout = 30000, retries = 2 } = options;
  
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const config = { 
        method, 
        url, 
        timeout: attempt === 1 ? timeout : timeout + 15000 // Add time for cold start on retry
      };
      if (data) config.data = data;
      
      const response = await axios(config);
      backendStatus.isAwake = true;
      backendStatus.lastCheck = Date.now();
      return response;
    } catch (error) {
      const isTimeout = error.code === 'ECONNABORTED' || error.message.includes('timeout');
      const isNetworkError = error.code === 'ERR_NETWORK' || !error.response;
      
      if ((isTimeout || isNetworkError) && attempt < retries) {
        console.log(`API call failed (attempt ${attempt}), retrying after wake-up...`);
        await wakeUpBackend(2);
        continue;
      }
      throw error;
    }
  }
};

// Booking Modal Component
const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    password: '',
    trailer: 'starcraft',
    start_date: '',
    end_date: '',
    terms_accepted: false,
    sms_consent: false,
    signature: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [serverWaking, setServerWaking] = useState(false);
  const [quote, setQuote] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Wake up backend when modal opens
  useEffect(() => {
    if (isOpen) {
      const wakeUp = async () => {
        if (!backendStatus.isAwake) {
          setServerWaking(true);
          await wakeUpBackend(3);
          setServerWaking(false);
        }
      };
      wakeUp();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    setConflictMessage('');
  };

  const checkAvailability = async () => {
    if (!formData.trailer || !formData.start_date || !formData.end_date) return;
    
    try {
      const response = await apiCall('post', `${API}/bookings/check-conflict`, {
        trailer: formData.trailer,
        start_date: formData.start_date,
        end_date: formData.end_date
      }, { timeout: 20000, retries: 2 });
      
      if (response.data.has_conflict) {
        setConflictMessage(response.data.message);
      } else {
        setConflictMessage('');
      }
    } catch (err) {
      console.error('Error checking availability:', err);
      // Don't show error for availability check - allow user to continue
    }
  };

  useEffect(() => {
    if (formData.start_date && formData.end_date && formData.trailer) {
      checkAvailability();
    }
  }, [formData.start_date, formData.end_date, formData.trailer]);

  // Fetch live price quote whenever dates change
  useEffect(() => {
    const fetchQuote = async () => {
      if (!formData.start_date || !formData.end_date || formData.end_date <= formData.start_date) {
        setQuote(null);
        return;
      }
      try {
        const response = await apiCall('post', `${API}/pricing/quote`, {
          start_date: formData.start_date,
          end_date: formData.end_date
        }, { timeout: 20000, retries: 2 });
        setQuote(response.data);
      } catch (err) {
        console.error('Error fetching price quote:', err);
        setQuote(null);
      }
    };
    fetchQuote();
  }, [formData.start_date, formData.end_date]);

  // Signature pad functions
  const startDrawing = (e) => {
    e.preventDefault(); // Prevent scrolling on touch
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling on touch
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault(); // Prevent scrolling on touch
    if (isDrawing) {
      const canvas = canvasRef.current;
      setFormData(prev => ({ ...prev, signature: canvas.toDataURL() }));
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setFormData(prev => ({ ...prev, signature: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!formData.terms_accepted) {
      setError('You must accept the rental terms to proceed.');
      setLoading(false);
      return;
    }

    try {
      // Use apiCall with retry for cold starts
      const response = await apiCall('post', `${API}/bookings`, formData, { timeout: 30000, retries: 2 });
      setSuccess(true);
      setBookingId(response.data.id);
    } catch (err) {
      console.error('Booking error:', err);
      
      // Check for network/timeout errors first
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setError('The request timed out. The server may be busy. Please wait a moment and try again.');
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        setError('Unable to connect to the server. Please wait a moment and try again, or contact us directly at the phone number on the Contact page.');
      } else {
        const errorDetail = err.response?.data?.detail || 'An error occurred. Please try again.';
        
        // Parse error type from message
        if (errorDetail.includes('ERROR_DATE_CONFLICT') || err.response?.status === 409) {
          // Remove the error code prefix for display
          const cleanMessage = errorDetail.replace('ERROR_DATE_CONFLICT: ', '');
          setConflictMessage(cleanMessage);
        } else if (errorDetail.includes('ERROR_SATURDAY_PICKUP') || errorDetail.includes('ERROR_SUNDAY_PICKUP')) {
          const cleanMessage = errorDetail.replace(/ERROR_\w+: /, '');
          setError(cleanMessage);
        } else if (errorDetail.includes('ERROR_')) {
          // Remove error code prefix for cleaner display
          const cleanMessage = errorDetail.replace(/ERROR_\w+: /, '');
          setError(cleanMessage);
        } else {
          setError(errorDetail);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="booking-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="modal-close">
          <i className="fas fa-times"></i>
        </button>
        
        {success ? (
          <div className="booking-success" data-testid="booking-success">
            <i className="fas fa-check-circle success-icon"></i>
            <h2>Booking Request Submitted!</h2>
            <p>Your booking reference: <strong>{bookingId.substring(0, 8)}</strong></p>
            <p>To complete your reservation, please submit your $500 deposit:</p>
            <a
              href={DEPOSIT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="deposit-button"
              data-testid="deposit-button"
            >
              <i className="fas fa-credit-card"></i> Submit Deposit ($500)
            </a>
            <p className="deposit-note">
              Your deposit will convert to your damage deposit at pickup.
              You will receive a confirmation email with all details.
            </p>
            <div className="dashboard-note" data-testid="dashboard-note">
              <i className="fas fa-user-circle"></i> <strong>Come back anytime:</strong> Log in to this website with your <strong>email and password</strong> to pay your deposit or pay your full rental amount from your Customer Dashboard before pickup.
            </div>
            <Link to="/login" className="dashboard-login-link" onClick={onClose} data-testid="success-login-link">
              <i className="fas fa-sign-in-alt"></i> Go to Customer Login
            </Link>
          </div>
        ) : (
          <>
            <h2><i className="fas fa-calendar-check"></i> Book Your Rental</h2>
            
            {serverWaking && (
              <div className="info-message" style={{backgroundColor: '#d1ecf1', color: '#0c5460', padding: '12px', borderRadius: '8px', marginBottom: '16px'}}>
                <i className="fas fa-spinner fa-spin"></i> Connecting to server... This may take a moment on first load.
              </div>
            )}
            
            {error && <div className="error-message" data-testid="error-message"><i className="fas fa-exclamation-circle"></i> {error}</div>}
            {conflictMessage && (
              <div className="conflict-message" data-testid="conflict-message">
                <i className="fas fa-exclamation-triangle"></i> {conflictMessage}
              </div>
            )}
            
            <form onSubmit={handleSubmit} data-testid="booking-form">
              <div className="form-group">
                <label><i className="fas fa-user"></i> Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="input-name"
                />
              </div>
              
              <div className="form-group">
                <label><i className="fas fa-map-marker-alt"></i> Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  data-testid="input-address"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label><i className="fas fa-phone"></i> Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="input-phone"
                  />
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-envelope"></i> Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label><i className="fas fa-lock"></i> Create Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  placeholder="Minimum 6 characters"
                  data-testid="input-password"
                />
                <small className="field-hint">
                  Use this with your email to log in later and access your Customer Dashboard (pay deposit, pay rental total, view details).
                </small>
              </div>
              
              <div className="sms-consent-group">
                <input
                  type="checkbox"
                  name="sms_consent"
                  id="sms_consent"
                  checked={formData.sms_consent}
                  onChange={handleChange}
                  data-testid="checkbox-sms-consent"
                />
                <label htmlFor="sms_consent">
                  I agree to receive automated text messages from Shoreline RV Rental at the phone number provided,
                  regarding my booking (confirmation, deposit &amp; payment reminders, return notices, and availability alerts).
                  Consent is not a condition of rental. Message frequency varies (approx. 3-10 msgs per booking).
                  Msg &amp; data rates may apply. Reply <strong>STOP</strong> to cancel, <strong>HELP</strong> for help.
                  View our <a href="/sms-terms" target="_blank" rel="noopener noreferrer">SMS Terms</a> and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </label>
              </div>
              
              <div className="form-group">
                <label><i className="fas fa-caravan"></i> Select Trailer *</label>
                <select
                  name="trailer"
                  value={formData.trailer}
                  onChange={handleChange}
                  required
                  data-testid="select-trailer"
                >
                  <option value="starcraft">2013 StarCraft 26BH (#1)</option>
                  <option value="amerilite">2006 Ameri-lite 25BH (#2)</option>
                </select>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label><i className="fas fa-calendar"></i> Start Date *</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="input-start-date"
                  />
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-calendar"></i> End Date *</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    required
                    min={formData.start_date || new Date().toISOString().split('T')[0]}
                    data-testid="input-end-date"
                  />
                </div>
              </div>
              
              {quote && (
                <div className={`price-quote ${quote.errors && quote.errors.length ? 'has-errors' : ''}`} data-testid="price-quote">
                  {quote.errors && quote.errors.length > 0 ? (
                    quote.errors.map((err, i) => (
                      <div key={i} className="quote-error" data-testid="quote-error">
                        <i className="fas fa-exclamation-triangle"></i> {err}
                      </div>
                    ))
                  ) : (
                    <>
                      {quote.notes && quote.notes.map((note, i) => (
                        <div key={i} className="quote-note" data-testid="quote-note">
                          <i className="fas fa-info-circle"></i> {note}
                        </div>
                      ))}
                      <div className="quote-row">
                        <span>Rental ({quote.nights} night{quote.nights !== 1 ? 's' : ''})</span>
                        <span data-testid="quote-subtotal">${quote.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="quote-row">
                        <span>Tax (7.375%)</span>
                        <span data-testid="quote-tax">${quote.tax.toFixed(2)}</span>
                      </div>
                      <div className="quote-row quote-total">
                        <span>Rental Total</span>
                        <span data-testid="quote-total">${quote.total.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              <div className="terms-section">
                <h3>Rental Terms Agreement</h3>
                <div className="terms-box">
                  <p><strong>Rental Rates:</strong></p>
                  <ul>
                    <li>Minimum rental: $399 + Tax</li>
                    <li>Extra day: $139</li>
                    <li>Weekly rate: $699</li>
                  </ul>
                  <p><strong>Deposit:</strong> $500 due at reservation (converts to damage deposit at pickup)</p>
                  <p><strong>Pickup Times:</strong> Mon-Thur 12:00-4:00 PM, Friday 12:00-2:00 PM</p>
                  <p><strong>Returns:</strong> Due by 11:00 AM. Late returns: $200 + daily fee</p>
                  <p><strong>Cancellation:</strong> 30+ days: $50 charge | 10-30 days: Loss of deposit | Within 10 days: Full charge</p>
                  <p><strong>Rules:</strong> No smoking, no pets. Camper must be returned clean.</p>
                  <a href="/rental-terms.pdf" target="_blank" className="terms-link">
                    View Full Rental Terms <i className="fas fa-external-link-alt"></i>
                  </a>
                </div>
                
                <div className="signature-section">
                  <label>Digital Signature</label>
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={150}
                    className="signature-canvas"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    data-testid="signature-canvas"
                  />
                  <button type="button" className="clear-signature" onClick={clearSignature}>
                    <i className="fas fa-eraser"></i> Clear Signature
                  </button>
                </div>
                
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    name="terms_accepted"
                    id="terms_accepted"
                    checked={formData.terms_accepted}
                    onChange={handleChange}
                    data-testid="checkbox-terms"
                  />
                  <label htmlFor="terms_accepted">
                    I have read and agree to the <a href="/rental-terms.pdf" target="_blank">Rental Terms and Conditions</a> *
                  </label>
                </div>
              </div>
              
              <button
                type="submit"
                className="submit-button"
                disabled={loading || !formData.terms_accepted || conflictMessage || (quote && quote.errors && quote.errors.length > 0)}
                data-testid="submit-booking"
              >
                {loading ? (
                  <><i className="fas fa-spinner fa-spin"></i> Processing...</>
                ) : (
                  <><i className="fas fa-paper-plane"></i> Submit Booking Request</>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = ({ onBookClick }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [videoDropdownOpen, setVideoDropdownOpen] = useState(false);
  const location = useLocation();

  return (
    <nav className="main-nav" data-testid="main-nav">
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          <i className="fas fa-caravan"></i>
          <span>Shoreline RV Rental</span>
        </Link>
        
        <button className="mobile-menu-toggle" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
          <i className={`fas ${menuOpen ? 'fa-times' : 'fa-bars'}`}></i>
        </button>
        
        <ul className={`nav-links ${menuOpen ? 'active' : ''}`}>
          <li>
            <Link to="/" className={location.pathname === '/' ? 'active' : ''} data-testid="nav-home">
              <i className="fas fa-home"></i> Home
            </Link>
          </li>
          <li>
            <Link to="/trailers" className={location.pathname === '/trailers' ? 'active' : ''} data-testid="nav-trailers">
              <i className="fas fa-caravan"></i> Trailers
            </Link>
          </li>
          <li className="dropdown">
            <button
              className="dropdown-toggle"
              onClick={() => setVideoDropdownOpen(!videoDropdownOpen)}
              data-testid="nav-video-dropdown"
            >
              <i className="fas fa-video"></i> Video Library <i className="fas fa-chevron-down"></i>
            </button>
            <ul className={`dropdown-menu ${videoDropdownOpen ? 'active' : ''}`}>
              <li>
                <a
                  href="https://www.youtube.com/playlist?list=PLp0zguzuPKV7FtLVo6rjObvSIIhAkhzkB"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-starcraft-videos"
                >
                  <i className="fab fa-youtube"></i> StarCraft 26BH Videos
                </a>
              </li>
              <li>
                <a
                  href="https://www.youtube.com/playlist?list=PLp0zguzuPKV61m-K8QZzFucn1FQqaQ8ui"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-amerilite-videos"
                >
                  <i className="fab fa-youtube"></i> Ameri-lite 25BH Videos
                </a>
              </li>
            </ul>
          </li>
          <li>
            <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''} data-testid="nav-contact">
              <i className="fas fa-phone"></i> Contact
            </Link>
          </li>
          <li>
            {getAuth() ? (
              <Link to="/dashboard" className={location.pathname === '/dashboard' ? 'active' : ''} data-testid="nav-dashboard">
                <i className="fas fa-user-circle"></i> My Dashboard
              </Link>
            ) : (
              <Link to="/login" className={location.pathname === '/login' ? 'active' : ''} data-testid="nav-login">
                <i className="fas fa-sign-in-alt"></i> Login
              </Link>
            )}
          </li>
          <li>
            <button className="nav-book-btn" onClick={onBookClick} data-testid="nav-book-rental">
              <i className="fas fa-calendar-plus"></i> Book Rental
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

// Home Page
const HomePage = ({ onBookClick }) => {
  return (
    <div className="home-page" data-testid="home-page">
      <section className="hero">
        <div className="hero-content">
          <h1>Shoreline RV Rental</h1>
          <p className="tagline">Alexandria, MN's Premier RV Rental Experience</p>
          <p className="hero-subtitle">Explore Minnesota's Lakes Country in Comfort</p>
          <div className="hero-actions">
            <button className="cta-button primary" onClick={onBookClick} data-testid="hero-book-btn">
              <i className="fas fa-calendar-check"></i> Book Your Adventure
            </button>
            <Link to="/trailers" className="cta-button secondary" data-testid="hero-view-trailers">
              <i className="fas fa-caravan"></i> View Our Trailers
            </Link>
          </div>
          <div className="hero-phone">
            <a href="tel:3206346167" data-testid="hero-phone">
              <i className="fas fa-phone-alt"></i> (320) 634-6167
            </a>
          </div>
        </div>
      </section>

      <section className="calendar-section" data-testid="calendar-section">
        <div className="container">
          <h2 className="section-title">
            <i className="fas fa-calendar-alt"></i> Rental Availability Calendar
          </h2>
          <p className="section-subtitle">Check availability and plan your trip</p>
          <div className="calendar-wrapper">
            <iframe
              src="https://calendar.google.com/calendar/embed?src=d0e538cdb4da82376f6de77f08400c9f1abcde52704677ce9f4f0cbc3f61b1ad%40group.calendar.google.com&ctz=America%2FChicago"
              title="Rental Calendar"
              frameBorder="0"
              scrolling="no"
              data-testid="google-calendar"
            ></iframe>
          </div>
        </div>
      </section>

      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Why Choose Shoreline RV?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <i className="fas fa-dollar-sign"></i>
              <h3>Affordable Rates</h3>
              <p>Starting at $399 for minimum rental, $699 weekly</p>
            </div>
            <div className="feature-card">
              <i className="fas fa-bed"></i>
              <h3>Sleep Up To 10</h3>
              <p>Spacious bunk house trailers perfect for families</p>
            </div>
            <div className="feature-card">
              <i className="fas fa-map-marked-alt"></i>
              <h3>Prime Location</h3>
              <p>Located in Alexandria, MN - Gateway to Lakes Country</p>
            </div>
            <div className="feature-card">
              <i className="fas fa-headset"></i>
              <h3>Local Support</h3>
              <p>Friendly local service with how-to video guides</p>
            </div>
          </div>
        </div>
      </section>

      <section className="trailers-preview">
        <div className="container">
          <h2 className="section-title">Our Rental Trailers</h2>
          <div className="trailers-grid">
            <div className="trailer-card">
              <img src="https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?w=400" alt="StarCraft 26BH" />
              <div className="trailer-info">
                <h3>2013 StarCraft 26BH (#1)</h3>
                <p>Sleeps 10 | 29' 4" Length</p>
                <Link to="/trailers" className="view-details">View Details <i className="fas fa-arrow-right"></i></Link>
              </div>
            </div>
            <div className="trailer-card">
              <img src="https://images.unsplash.com/photo-1533745848184-3db07256e163?w=400" alt="Ameri-lite 25BH" />
              <div className="trailer-info">
                <h3>2006 Ameri-lite 25BH (#2)</h3>
                <p>Sleeps 6 | 26' 8" Length</p>
                <Link to="/trailers" className="view-details">View Details <i className="fas fa-arrow-right"></i></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Ready to Start Your Adventure?</h2>
          <p>Book your rental today and explore Minnesota's beautiful lakes country!</p>
          <button className="cta-button primary large" onClick={onBookClick}>
            <i className="fas fa-calendar-check"></i> Reserve Now
          </button>
        </div>
      </section>
    </div>
  );
};

// Trailers Page
const TrailersPage = ({ onBookClick }) => {
  return (
    <div className="trailers-page" data-testid="trailers-page">
      <section className="page-header">
        <h1>Travel Trailer Rental Information for 2025</h1>
        <p>Call Us to Reserve your Rental Today! <a href="tel:3206346167">(320) 634-6167</a></p>
      </section>

      <section className="rental-info">
        <div className="container">
          <div className="info-box">
            <h2>Rental Rates & Policies</h2>
            <ul>
              <li><strong>Minimum rental:</strong> $399 + Tax (covers up to 3 nights)</li>
              <li><strong>Extra day:</strong> $139</li>
              <li><strong>Weekly rate:</strong> $699 + Tax (add $139/day beyond a week)</li>
              <li><strong>Rental season:</strong> May 1 through September 30 only</li>
              <li>Sales tax of 7.375% applies to all rentals</li>
              <li>All rentals to be paid in full at time of camper pickup</li>
              <li><strong>Pick up Time:</strong> Mon-Thur 12:00 - 4:00 PM, Friday 12:00 - 2:00 PM. NO EXCEPTIONS!</li>
              <li>No rental pickups on Saturdays, Sundays or Holidays</li>
              <li>No rentals with return dates on Saturday</li>
              <li>Returns accepted early on Sundays/Holidays - no refunds for early returns</li>
              <li>Returns due by 11:00am on scheduled day of return. Late returns: $200 + daily rental fee</li>
              <li><strong>Deposit:</strong> $500 due at reservation (converts to Damage Deposit at pickup)</li>
              <li>Cash or Credit Cards only for Rental/Damage Deposit</li>
              <li>Rentals overlapping a holiday require Weekly Rental Fee</li>
              <li>Music Festival rentals require Weekly Fee + $1000 CASH Damage Deposit</li>
              <li>Camper must be returned clean, no damage. Smoking/Pets forfeit Damage Deposit</li>
            </ul>

            <h3>Cancellation Policy</h3>
            <ul>
              <li>30 days prior - $50 Charge</li>
              <li>10-30 days prior - Loss of Deposit</li>
              <li>Within 10 Days - Full Rental Charge</li>
            </ul>

            <h3>Vehicle Requirements</h3>
            <ul>
              <li>Six-cylinder or larger engine</li>
              <li>Permanently installed hitch</li>
              <li>2 5/16 inch ball</li>
              <li>Pre-wired for towing</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="trailers-list">
        <div className="container">
          {/* StarCraft Trailer */}
          <div className="trailer-detail" data-testid="starcraft-trailer">
            <h3 className="trailer-title">2013 StarCraft 26BH (#1)</h3>
            
            <PhotoGallery trailer="starcraft" title="StarCraft 26BH" />
            
            <div className="video-library-btn">
              <a
                href="https://www.youtube.com/playlist?list=PLp0zguzuPKV7FtLVo6rjObvSIIhAkhzkB"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-video"
                data-testid="starcraft-video-btn"
              >
                <i className="fab fa-youtube"></i> How To Video Library - StarCraft
              </a>
            </div>

            <div className="trailer-specs">
              <div className="spec-group">
                <h4><i className="fas fa-weight-hanging"></i> Weights</h4>
                <ul>
                  <li>Unloaded Vehicle Weight: 4,140 lbs</li>
                  <li>Dry Hitch Weight: 455 lbs</li>
                  <li>Gross Vehicle Weight Rating: 6,500 lbs</li>
                  <li>Cargo Carrying Capacity: 2,360 lbs</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-ruler"></i> Measurements</h4>
                <ul>
                  <li>Exterior Length: 29' 4"</li>
                  <li>Exterior Height w/ AC: 127"</li>
                  <li>Interior Height: 81"</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-tint"></i> Tank Capacities</h4>
                <ul>
                  <li>Fresh Water (incl. water heater): 48 gals</li>
                  <li>Gray Waste Water: 32.5 gals</li>
                  <li>Black Waste Water: 32.5 gals</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-bed"></i> Other</h4>
                <ul>
                  <li>Sleeping Capacity: 10</li>
                  <li>Queen bed, Bunk Beds, sofa bed, dinette bed</li>
                  <li>Absolutely no Pets/Smoking</li>
                  <li>Propane is full - no need to refill</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Ameri-lite Trailer */}
          <div className="trailer-detail" data-testid="amerilite-trailer">
            <h3 className="trailer-title">2006 Ameri-lite 25BH (#2)</h3>
            
            <PhotoGallery trailer="amerilite" title="Ameri-lite 25BH" />
            
            <div className="video-library-btn">
              <a
                href="https://www.youtube.com/playlist?list=PLp0zguzuPKV61m-K8QZzFucn1FQqaQ8ui"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-video"
                data-testid="amerilite-video-btn"
              >
                <i className="fab fa-youtube"></i> How To Video Library - Ameri-lite
              </a>
            </div>

            <div className="trailer-specs">
              <div className="spec-group">
                <h4><i className="fas fa-weight-hanging"></i> Weights</h4>
                <ul>
                  <li>Unloaded Vehicle Weight: 3,940 lbs</li>
                  <li>Dry Hitch Weight: 440 lbs</li>
                  <li>Gross Vehicle Weight Rating: 5,920 lbs</li>
                  <li>Cargo Carrying Capacity: 1,524 lbs</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-ruler"></i> Measurements</h4>
                <ul>
                  <li>Exterior Length: 26' 8"</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-tint"></i> Tank Capacities</h4>
                <ul>
                  <li>Fresh Water (incl. water heater): 30 gals</li>
                  <li>Gray Waste Water: 37 gals</li>
                  <li>Black Waste Water: 37 gals</li>
                </ul>
              </div>

              <div className="spec-group">
                <h4><i className="fas fa-bed"></i> Other</h4>
                <ul>
                  <li>Sleeping Capacity: 6</li>
                  <li>Sofa was removed - perfect for Pack-n-Play</li>
                  <li>Absolutely no Pets/Smoking</li>
                  <li>Propane is full - no need to refill</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="book-cta">
        <div className="container">
          <h2>Ready to Book?</h2>
          <button className="cta-button primary large" onClick={onBookClick} data-testid="trailers-book-btn">
            <i className="fas fa-calendar-check"></i> Book Your Rental Now
          </button>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const ContactPage = ({ onBookClick }) => {
  return (
    <div className="contact-page" data-testid="contact-page">
      <section className="page-header">
        <h1>Contact Us</h1>
        <p>We're here to help you plan your perfect camping trip!</p>
      </section>

      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2><i className="fas fa-map-marker-alt"></i> Our Location</h2>
              <address>
                <strong>Shoreline RV</strong><br />
                609 N Nokomis St<br />
                Alexandria, MN 56308
              </address>

              <h3><i className="fas fa-phone-alt"></i> Phone</h3>
              <p><a href="tel:3206346167">(320) 634-6167</a></p>

              <h3><i className="fas fa-envelope"></i> Email</h3>
              <p><a href="mailto:info@shorelinervrental.com">info@shorelinervrental.com</a></p>

              <h3><i className="fas fa-clock"></i> Hours</h3>
              <p>
                Pickup: Mon-Thur 12:00 - 4:00 PM<br />
                Friday: 12:00 - 2:00 PM<br />
                No pickups on weekends or holidays
              </p>

              <button className="cta-button primary" onClick={onBookClick} data-testid="contact-book-btn">
                <i className="fas fa-calendar-check"></i> Book Your Rental
              </button>
            </div>

            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2798.1234567890!2d-95.36672957418314!3d45.89726199338206!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDUzJzUwLjEiTiA5NcKwMjInMDAuMiJX!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Shoreline RV Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="main-footer" data-testid="main-footer">
      <div className="footer-content">
        <div className="footer-section">
          <h4><i className="fas fa-caravan"></i> Shoreline RV Rental</h4>
          <p>Your trusted RV rental service in Alexandria, Minnesota.</p>
        </div>
        
        <div className="footer-section">
          <h4>Contact</h4>
          <p>609 N Nokomis St<br />Alexandria, MN 56308</p>
          <p><a href="tel:3206346167">(320) 634-6167</a></p>
          <p><a href="mailto:info@shorelinervrental.com">info@shorelinervrental.com</a></p>
        </div>
        
        <div className="footer-section">
          <h4>Quick Links</h4>
          <ul>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/trailers">Trailers</Link></li>
            <li><Link to="/contact">Contact</Link></li>
            <li><a href="/rental-terms.pdf" target="_blank">Rental Terms</a></li>
            <li><Link to="/sms-terms">SMS Terms</Link></li>
            <li><Link to="/privacy-policy">Privacy Policy</Link></li>
          </ul>
        </div>
        
        <div className="footer-section">
          <h4>Video Guides</h4>
          <ul>
            <li>
              <a href="https://www.youtube.com/playlist?list=PLp0zguzuPKV7FtLVo6rjObvSIIhAkhzkB" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-youtube"></i> StarCraft How-To
              </a>
            </li>
            <li>
              <a href="https://www.youtube.com/playlist?list=PLp0zguzuPKV61m-K8QZzFucn1FQqaQ8ui" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-youtube"></i> Ameri-lite How-To
              </a>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} Shoreline RV Rental. All rights reserved.</p>
      </div>
    </footer>
  );
};

// Customer Login Page
const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getAuth()) navigate('/dashboard');
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await apiCall('post', `${API}/auth/login`, { email, password }, { timeout: 30000, retries: 2 });
      setAuthSession(response.data.token, response.data.user);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to log in. Please check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <div className="auth-card">
        <h1><i className="fas fa-user-circle"></i> Customer Login</h1>
        <p className="auth-subtitle">Log in with the email and password you created when booking your rental.</p>
        
        {error && <div className="error-message" data-testid="login-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
        
        <form onSubmit={handleSubmit} data-testid="login-form">
          <div className="form-group">
            <label><i className="fas fa-envelope"></i> Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
            />
          </div>
          <div className="form-group">
            <label><i className="fas fa-lock"></i> Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
            />
          </div>
          <button type="submit" className="submit-button" disabled={loading} data-testid="login-submit">
            {loading ? <><i className="fas fa-spinner fa-spin"></i> Logging in...</> : <><i className="fas fa-sign-in-alt"></i> Log In</>}
          </button>
        </form>
        
        <p className="auth-footnote">
          Don't have an account? Accounts are created automatically when you book a rental.
          Forgot your password? Call us at <a href="tel:3206346167">(320) 634-6167</a>.
        </p>
      </div>
    </div>
  );
};

// Inline date-change form for a dashboard booking card
const DateChangeForm = ({ booking, token, onUpdated, onCancel }) => {
  const [startDate, setStartDate] = useState(booking.start_date);
  const [endDate, setEndDate] = useState(booking.end_date);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!startDate || !endDate || endDate <= startDate) {
        setQuote(null);
        return;
      }
      try {
        const response = await axios.post(`${API}/pricing/quote`, { start_date: startDate, end_date: endDate }, { timeout: 20000 });
        setQuote(response.data);
      } catch (err) {
        setQuote(null);
      }
    };
    fetchQuote();
  }, [startDate, endDate]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.patch(`${API}/bookings/${booking.id}/dates`, { start_date: startDate, end_date: endDate }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      onUpdated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail.replace(/ERROR_\w+: /, '') : 'Unable to change dates. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="date-change-form" data-testid="date-change-form">
      <h4><i className="fas fa-calendar-alt"></i> Change Rental Dates</h4>
      <p className="date-change-hint">New dates are checked for availability. You'll receive an SMS and email confirming the change.</p>
      {error && <div className="error-message" data-testid="date-change-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
      <div className="date-change-inputs">
        <div className="form-group">
          <label>New Pickup Date</label>
          <input
            type="date"
            value={startDate}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="change-start-date"
          />
        </div>
        <div className="form-group">
          <label>New Return Date</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
            data-testid="change-end-date"
          />
        </div>
      </div>
      {quote && (
        <div className={`price-quote ${quote.errors && quote.errors.length ? 'has-errors' : ''}`} data-testid="change-price-quote">
          {quote.errors && quote.errors.length > 0 ? (
            quote.errors.map((err, i) => (
              <div key={i} className="quote-error"><i className="fas fa-exclamation-triangle"></i> {err}</div>
            ))
          ) : (
            <>
              {quote.notes && quote.notes.map((note, i) => (
                <div key={i} className="quote-note"><i className="fas fa-info-circle"></i> {note}</div>
              ))}
              <div className="quote-row"><span>Rental ({quote.nights} night{quote.nights !== 1 ? 's' : ''})</span><span>${quote.subtotal.toFixed(2)}</span></div>
              <div className="quote-row"><span>Tax (7.375%)</span><span>${quote.tax.toFixed(2)}</span></div>
              <div className="quote-row quote-total"><span>New Rental Total</span><span data-testid="change-quote-total">${quote.total.toFixed(2)}</span></div>
            </>
          )}
        </div>
      )}
      <div className="date-change-actions">
        <button
          className="submit-button"
          onClick={save}
          disabled={saving || !quote || (quote.errors && quote.errors.length > 0)}
          data-testid="save-dates-btn"
        >
          {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-check"></i> Confirm New Dates</>}
        </button>
        <button className="btn-cancel-change" onClick={onCancel} disabled={saving} data-testid="cancel-dates-btn">
          Cancel
        </button>
      </div>
    </div>
  );
};

// Customer Dashboard Page
const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const auth = getAuth();

  const fetchBookings = async () => {
    try {
      await wakeUpBackend(3);
      const response = await axios.get(`${API}/customer/bookings`, {
        headers: { Authorization: `Bearer ${auth.token}` },
        timeout: 30000
      });
      setBookings(response.data.bookings || []);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuthSession();
        navigate('/login');
        return;
      }
      setError('Unable to load your bookings. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!auth) {
      navigate('/login');
      return;
    }
    fetchBookings();
  }, []);

  const handleLogout = () => {
    clearAuthSession();
    navigate('/login');
  };

  const trailerName = (code) => code === 'starcraft' ? '2013 StarCraft 26BH (#1)' : '2006 Ameri-lite 25BH (#2)';

  if (!auth) return null;

  return (
    <div className="dashboard-page" data-testid="customer-dashboard">
      <section className="page-header">
        <h1><i className="fas fa-user-circle"></i> My Dashboard</h1>
        <p>Welcome back, {auth.user.name || auth.user.email}!</p>
      </section>

      <section className="dashboard-content">
        <div className="container">
          <div className="dashboard-toolbar">
            <button className="btn-logout" onClick={handleLogout} data-testid="logout-btn">
              <i className="fas fa-sign-out-alt"></i> Log Out
            </button>
          </div>

          {/* Terms & Conditions */}
          <div className="admin-card" data-testid="dashboard-terms-card">
            <h2><i className="fas fa-file-contract"></i> Rental Terms & Conditions</h2>
            <button className="terms-toggle" onClick={() => setShowTerms(!showTerms)} data-testid="terms-toggle">
              {showTerms ? 'Hide Terms Summary' : 'Review Terms Summary'} <i className={`fas fa-chevron-${showTerms ? 'up' : 'down'}`}></i>
            </button>
            {showTerms && (
              <div className="terms-box" data-testid="dashboard-terms-box">
                <p><strong>Rental Rates:</strong></p>
                <ul>
                  <li>Minimum rental: $399 + Tax (covers up to 3 nights)</li>
                  <li>Extra day: $139 | Weekly rate: $699 + Tax</li>
                  <li>Rental season: May 1 - September 30</li>
                </ul>
                <p><strong>Deposit:</strong> $500 due at reservation (converts to damage deposit at pickup)</p>
                <p><strong>Pickup Times:</strong> Mon-Thur 12:00-4:00 PM, Friday 12:00-2:00 PM. No Sat/Sun/holiday pickups.</p>
                <p><strong>Returns:</strong> Due by 11:00 AM. Late returns: $200 + daily fee</p>
                <p><strong>Cancellation:</strong> 30+ days: $50 charge | 10-30 days: Loss of deposit | Within 10 days: Full charge</p>
                <p><strong>Rules:</strong> No smoking, no pets. Camper must be returned clean.</p>
              </div>
            )}
            <a href="/rental-terms.pdf" target="_blank" rel="noopener noreferrer" className="terms-link" data-testid="dashboard-terms-pdf">
              View Full Rental Terms (PDF) <i className="fas fa-external-link-alt"></i>
            </a>
          </div>

          {/* Bookings */}
          <div className="admin-card">
            <h2><i className="fas fa-calendar-alt"></i> My Rentals</h2>
            {loading ? (
              <p><i className="fas fa-spinner fa-spin"></i> Loading your rentals...</p>
            ) : error ? (
              <div className="error-message"><i className="fas fa-exclamation-circle"></i> {error}</div>
            ) : bookings.length === 0 ? (
              <p className="no-bookings" data-testid="no-bookings">No rentals found for your account yet.</p>
            ) : (
              <div className="customer-bookings">
                {bookings.map((b) => (
                  <div className="customer-booking-card" key={b.id} data-testid={`booking-card-${b.id.substring(0, 8)}`}>
                    <div className="booking-card-header">
                      <h3><i className="fas fa-caravan"></i> {trailerName(b.trailer)}</h3>
                      <span className={`status-badge status-${b.status}`}>{b.status.replace(/_/g, ' ')}</span>
                    </div>
                    
                    {b.conflict_notice_sent && b.status === 'pending' && (
                      <div className="conflict-message" data-testid="displaced-notice">
                        <i className="fas fa-exclamation-triangle"></i> These dates were booked by another customer before your deposit was received. Please call (320) 634-6167 to rebook.
                      </div>
                    )}
                    
                    <div className="booking-card-details">
                      <div className="booking-detail">
                        <span className="detail-label">Booking Ref</span>
                        <span>{b.id.substring(0, 8)}</span>
                      </div>
                      <div className="booking-detail">
                        <span className="detail-label">Pickup</span>
                        <span data-testid="booking-pickup">{b.start_date}</span>
                      </div>
                      <div className="booking-detail">
                        <span className="detail-label">Return (by 11 AM)</span>
                        <span data-testid="booking-return">{b.effective_end_date || b.end_date}</span>
                      </div>
                    </div>
                    
                    {b.weekend_special && (
                      <p className="weekend-note"><i className="fas fa-info-circle"></i> Weekend special: Friday pickup with Monday return at the minimum rate.</p>
                    )}
                    
                    {b.total != null && (
                      <div className="booking-pricing">
                        <div className="quote-row"><span>Rental</span><span>${b.subtotal.toFixed(2)}</span></div>
                        <div className="quote-row"><span>Tax (7.375%)</span><span>${b.tax.toFixed(2)}</span></div>
                        <div className="quote-row quote-total"><span>Rental Total</span><span data-testid="booking-total">${b.total.toFixed(2)}</span></div>
                      </div>
                    )}
                    
                    <div className="booking-payments">
                      {!b.deposit_paid && b.status === 'pending' && !b.conflict_notice_sent && (
                        <div className="payment-block">
                          <a href={b.deposit_link || DEPOSIT_LINK} target="_blank" rel="noopener noreferrer" className="deposit-button" data-testid="dashboard-deposit-btn">
                            <i className="fas fa-credit-card"></i> Pay $500 Deposit
                          </a>
                          <p className="payment-hint">Your dates are not held until your deposit is received and confirmed by our team.</p>
                        </div>
                      )}
                      {b.deposit_paid && (
                        <p className="paid-badge" data-testid="deposit-paid-badge"><i className="fas fa-check-circle"></i> Deposit Received</p>
                      )}
                      {!b.rental_paid && (
                        <div className="payment-block">
                          <a href={b.payment_link || RENTAL_PAYMENT_LINK} target="_blank" rel="noopener noreferrer" className="rental-pay-button" data-testid="dashboard-rental-pay-btn">
                            <i className="fas fa-money-check-alt"></i> Pay Rental Total{b.total != null ? ` ($${b.total.toFixed(2)})` : ''}
                          </a>
                          <p className="payment-hint">Rental total must be paid before pickup.</p>
                        </div>
                      )}
                      {b.rental_paid && (
                        <p className="paid-badge" data-testid="rental-paid-badge"><i className="fas fa-check-circle"></i> Rental Paid in Full</p>
                      )}
                    </div>
                    
                    {!b.rental_paid && !b.conflict_notice_sent && (
                      editingId === b.id ? (
                        <DateChangeForm
                          booking={b}
                          token={auth.token}
                          onUpdated={() => { setEditingId(null); setLoading(true); fetchBookings(); }}
                          onCancel={() => setEditingId(null)}
                        />
                      ) : (
                        <button
                          className="btn-change-dates"
                          onClick={() => setEditingId(b.id)}
                          data-testid={`change-dates-btn-${b.id.substring(0, 8)}`}
                        >
                          <i className="fas fa-calendar-alt"></i> Change Dates
                        </button>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

// Admin Page for Managing Bookings (Service Account handles calendar automatically)
const AdminPage = () => {
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [connectionError, setConnectionError] = useState(null);
  const [wakingUp, setWakingUp] = useState(false);

  useEffect(() => {
    initializeAdmin();
  }, []);

  const initializeAdmin = async () => {
    setLoading(true);
    setWakingUp(true);
    setConnectionError(null);
    
    // Wake up backend first (handles cold starts)
    const isAwake = await wakeUpBackend(3);
    setWakingUp(false);
    
    if (!isAwake) {
      setConnectionError('Server is starting up or unreachable. This may take a moment on first load. Click Retry to try again.');
      setCalendarStatus({ is_authorized: false, message: 'Server not responding' });
      setLoading(false);
      return;
    }
    
    // Now fetch data
    await Promise.all([checkCalendarStatus(), fetchBookings()]);
  };

  const checkCalendarStatus = async () => {
    try {
      const response = await apiCall('get', `${API}/calendar/status`, null, { timeout: 20000, retries: 2 });
      setCalendarStatus(response.data);
      setConnectionError(null);
    } catch (error) {
      console.error('Error checking calendar status:', error);
      let errorMessage = 'Error checking status';
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage = 'Connection timed out. The server may be waking up from sleep. Please click Retry.';
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        errorMessage = 'Cannot reach the server. The backend may be starting up. Please click Retry in a few seconds.';
      } else if (error.response) {
        errorMessage = `Server error (${error.response.status}): ${error.response.statusText}`;
      }
      
      setConnectionError(errorMessage);
      setCalendarStatus({ is_authorized: false, message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await apiCall('get', `${API}/bookings`, null, { timeout: 20000, retries: 2 });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (window.confirm('Are you sure you want to cancel this booking? This will also remove it from the calendar.')) {
      try {
        await apiCall('delete', `${API}/bookings/${bookingId}`, null, { timeout: 20000, retries: 2 });
        fetchBookings(); // Refresh the list
        alert('Booking cancelled successfully');
      } catch (error) {
        console.error('Error cancelling booking:', error);
        alert('Failed to cancel booking. The server may be busy. Please try again.');
      }
    }
  };

  const markDepositReceived = async (bookingId, customerName) => {
    if (window.confirm(`Confirm that you have received the deposit from ${customerName}? This will send a confirmation email to the customer.`)) {
      try {
        await apiCall('patch', `${API}/bookings/${bookingId}/deposit-paid`, {}, { timeout: 20000, retries: 2 });
        fetchBookings(); // Refresh the list
        alert('Deposit marked as received! Confirmation email sent to customer.');
      } catch (error) {
        console.error('Error marking deposit:', error);
        alert('Failed to update booking. The server may be busy. Please try again.');
      }
    }
  };

  const markRentalPaid = async (bookingId, customerName) => {
    if (window.confirm(`Confirm that ${customerName} has paid the rental total in full?`)) {
      try {
        await apiCall('patch', `${API}/bookings/${bookingId}/rental-paid`, {}, { timeout: 20000, retries: 2 });
        fetchBookings();
        alert('Rental marked as paid in full!');
      } catch (error) {
        console.error('Error marking rental paid:', error);
        alert('Failed to update booking. The server may be busy. Please try again.');
      }
    }
  };


  return (
    <div className="admin-page" data-testid="admin-page">
      <section className="page-header">
        <h1><i className="fas fa-cog"></i> Admin Dashboard</h1>
        <p>Manage your rental bookings</p>
      </section>

      <section className="admin-content">
        <div className="container">
          {/* Waking Up Server Message */}
          {wakingUp && (
            <div className="admin-card" style={{backgroundColor: '#d1ecf1', borderColor: '#bee5eb'}}>
              <h2 style={{color: '#0c5460'}}><i className="fas fa-spinner fa-spin"></i> Connecting to Server...</h2>
              <p style={{color: '#0c5460'}}>The server is waking up. This may take 10-20 seconds on first load.</p>
            </div>
          )}

          {/* Connection Error Alert */}
          {connectionError && !wakingUp && (
            <div className="admin-card" style={{backgroundColor: '#fff3cd', borderColor: '#ffc107'}}>
              <h2 style={{color: '#856404'}}><i className="fas fa-exclamation-triangle"></i> Connection Issue</h2>
              <p style={{color: '#856404'}}>{connectionError}</p>
              <p style={{color: '#856404', fontSize: '0.9em'}}>Backend URL: {API}</p>
              <button className="btn-connect" onClick={initializeAdmin} style={{marginTop: '10px'}}>
                <i className="fas fa-sync"></i> Retry Connection
              </button>
            </div>
          )}

          {/* Calendar Status Section */}
          <div className="admin-card">
            <h2><i className="fab fa-google"></i> Google Calendar Status</h2>
            
            {loading ? (
              <p><i className="fas fa-spinner fa-spin"></i> {wakingUp ? 'Waiting for server...' : 'Checking status...'}</p>
            ) : (
              <div className="calendar-status">
                {calendarStatus?.is_authorized ? (
                  <div className="status-connected">
                    <p><i className="fas fa-check-circle"></i> <strong>Connected via Service Account</strong></p>
                    <p>Service Account: {calendarStatus.admin_email}</p>
                    <p className="status-note">{calendarStatus.message}</p>
                    <p className="status-note">New bookings will automatically appear on your Google Calendar.</p>
                  </div>
                ) : (
                  <div className="status-disconnected">
                    <p><i className="fas fa-exclamation-circle"></i> <strong>Not Connected</strong></p>
                    <p>{calendarStatus?.message || 'Service Account not configured'}</p>
                    {!connectionError && (
                      <p className="status-note">Contact support to configure the service account.</p>
                    )}
                    <button className="btn-connect" onClick={checkCalendarStatus} style={{marginTop: '10px'}}>
                      <i className="fas fa-sync"></i> Retry
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bookings Section */}
          <div className="admin-card">
            <h2><i className="fas fa-calendar-alt"></i> Recent Bookings</h2>
            
            {bookings.length === 0 ? (
              <p className="no-bookings">No bookings yet.</p>
            ) : (
              <div className="bookings-table-wrapper">
                <table className="bookings-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Trailer</th>
                      <th>Dates</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id}>
                        <td>{booking.id.substring(0, 8)}</td>
                        <td>{booking.name}</td>
                        <td>{booking.email}</td>
                        <td>{booking.trailer === 'starcraft' ? 'StarCraft 26BH' : 'Ameri-lite 25BH'}</td>
                        <td>{booking.start_date} to {booking.effective_end_date || booking.end_date}</td>
                        <td>{booking.total != null ? `$${booking.total.toFixed(2)}` : '—'}</td>
                        <td>
                          <span className={`status-badge status-${booking.status}`}>
                            {booking.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            {booking.status === 'pending' && (
                              <button 
                                className="btn-deposit-received"
                                onClick={() => markDepositReceived(booking.id, booking.name)}
                                title="Mark Deposit as Received"
                              >
                                <i className="fas fa-check"></i> Received Deposit
                              </button>
                            )}
                            {booking.deposit_paid && !booking.rental_paid && (
                              <button 
                                className="btn-rental-paid"
                                onClick={() => markRentalPaid(booking.id, booking.name)}
                                title="Mark Rental as Paid in Full"
                                data-testid={`rental-paid-btn-${booking.id.substring(0, 8)}`}
                              >
                                <i className="fas fa-dollar-sign"></i> Received Rental Payment
                              </button>
                            )}
                            <button 
                              className="btn-cancel-booking"
                              onClick={() => cancelBooking(booking.id)}
                              title="Cancel Booking"
                            >
                              <i className="fas fa-times"></i> Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App Component
function App() {
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  const openBookingModal = () => setBookingModalOpen(true);
  const closeBookingModal = () => setBookingModalOpen(false);

  return (
    <div className="App">
      <BrowserRouter>
        <Navigation onBookClick={openBookingModal} />
        
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage onBookClick={openBookingModal} />} />
            <Route path="/trailers" element={<TrailersPage onBookClick={openBookingModal} />} />
            <Route path="/contact" element={<ContactPage onBookClick={openBookingModal} />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={<CustomerDashboard />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </main>
        
        <Footer />
        
        <BookingModal isOpen={bookingModalOpen} onClose={closeBookingModal} />
        
        {/* Floating Book Button */}
        <button
          className="floating-book-btn"
          onClick={openBookingModal}
          data-testid="floating-book-btn"
        >
          <i className="fas fa-calendar-plus"></i>
          <span>Book Rental</span>
        </button>
      </BrowserRouter>
    </div>
  );
}

export default App;
